import { useState } from 'react';
import { X } from 'lucide-react';
import { create } from 'zustand';
import { useAppKit } from '@reown/appkit/react';
import { useWalletModal } from './SolanaWalletModal';

interface NetworkModalState {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

export const useNetworkModal = create<NetworkModalState>((set) => ({
  isOpen: false,
  setOpen: (open) => set({ isOpen: open }),
}));

const networks = [
  { id: 'eth', name: 'Ethereum', symbol: 'ETH', icon: '/icons/eth.svg' },
  { id: 'bsc', name: 'BSC', symbol: 'BNB', icon: '/icons/bnb.svg' },
  { id: 'sol', name: 'Solana', symbol: 'SOL', icon: '/icons/sol.svg' }
];

export default function NetworkModal() {
  const { isOpen, setOpen } = useNetworkModal();
  const { open: openReown } = useAppKit();
  const { setOpen: openSolanaModal } = useWalletModal();
  const [error, setError] = useState('');

  const handleNetworkSelect = async (networkId: string) => {
    try {
      setError('');
      
      if (networkId === 'sol') {
        openSolanaModal(true);
        setOpen(false);
      } else {
        // For EVM chains, use AppKit's open method
        openReown();
        setOpen(false);
      }
    } catch (err) {
      console.error('Network selection failed:', err);
      setError('Failed to connect wallet. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-quantum-dark/80 backdrop-blur-lg flex items-center justify-center z-50">
      <div className="quantum-card w-full max-w-md p-6 relative">
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-quantum-light/50 hover:text-quantum-light"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-bold text-quantum-light mb-6">Select Network</h3>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {networks.map((network) => (
            <button
              key={network.id}
              onClick={() => handleNetworkSelect(network.id)}
              className="quantum-card w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors duration-200"
            >
              <img 
                src={network.icon} 
                alt={network.name} 
                className="w-8 h-8" 
              />
              <div className="text-left">
                <div className="font-medium text-quantum-light">{network.name}</div>
                <div className="text-sm text-quantum-light/70">Connect with {network.symbol}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}